import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { FileText, Shield, CreditCard, Truck, AlertCircle } from 'lucide-react';

const TermsConditionsPage: React.FC = () => {
  useEffect(() => {
    document.title = "Terms & Conditions | Strum Music";
  }, []);

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="bg-gray-800 py-12">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <FileText className="h-16 w-16 mx-auto mb-4 text-gray-300" />
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">Terms & Conditions</h1>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              Please read these terms carefully before using our services
            </p>
          </motion.div>
        </div>
      </section>

      {/* Terms Content */}
      <section className="py-12">
        <div className="container-custom max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="space-y-8"
          >
            {/* Introduction */}
            <div>
              <p className="text-gray-700 text-lg leading-relaxed">
                Welcome to Strum Music. These Terms and Conditions ("Terms") govern your use of our website 
                and services. By accessing or using our services, you agree to be bound by these Terms.
              </p>
              <p className="text-gray-600 text-sm mt-4">
                <strong>Last updated:</strong> January 2025
              </p>
            </div>

            {/* Acceptance of Terms */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Acceptance of Terms</h2>
              <div className="bg-blue-50 p-6 rounded-lg">
                <p className="text-gray-700">
                  By accessing and using the Strum Music website, mobile application, or any of our services, 
                  you acknowledge that you have read, understood, and agree to be bound by these Terms and 
                  Conditions and our Privacy Policy.
                </p>
              </div>
            </div>

            {/* Use of Services */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Use of Services</h2>
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">Eligibility</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• You must be at least 18 years old to make purchases</li>
                    <li>• You must provide accurate and complete information</li>
                    <li>• You are responsible for maintaining account security</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">Prohibited Uses</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Using our services for any unlawful purpose</li>
                    <li>• Attempting to gain unauthorized access to our systems</li>
                    <li>• Interfering with the proper functioning of our website</li>
                    <li>• Reproducing or distributing our content without permission</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Products and Pricing */}
            <div>
              <div className="flex items-center mb-4">
                <CreditCard className="h-6 w-6 text-green-600 mr-2" />
                <h2 className="text-2xl font-bold text-gray-900">3. Products and Pricing</h2>
              </div>
              <div className="bg-green-50 p-6 rounded-lg space-y-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">Product Information</h3>
                  <p className="text-gray-700">
                    We strive to provide accurate product descriptions, images, and specifications. 
                    However, we do not warrant that product descriptions are error-free, complete, 
                    or current.
                  </p>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">Pricing</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• All prices are in Indian Rupees (INR) and include applicable taxes</li>
                    <li>• Prices are subject to change without notice</li>
                    <li>• We reserve the right to correct pricing errors</li>
                    <li>• Promotional offers are subject to terms and availability</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Orders and Payment */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Orders and Payment</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="border rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-3">Order Processing</h3>
                  <ul className="space-y-2 text-gray-700 text-sm">
                    <li>• Orders are subject to acceptance and availability</li>
                    <li>• We may cancel orders for any reason</li>
                    <li>• Order confirmation does not guarantee acceptance</li>
                    <li>• Processing time: 1-2 business days</li>
                  </ul>
                </div>
                <div className="border rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-3">Payment Terms</h3>
                  <ul className="space-y-2 text-gray-700 text-sm">
                    <li>• Payment required at time of order</li>
                    <li>• We accept major credit/debit cards and UPI</li>
                    <li>• All transactions are secure and encrypted</li>
                    <li>• Failed payments may result in order cancellation</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Shipping and Delivery */}
            <div>
              <div className="flex items-center mb-4">
                <Truck className="h-6 w-6 text-blue-600 mr-2" />
                <h2 className="text-2xl font-bold text-gray-900">5. Shipping and Delivery</h2>
              </div>
              <div className="bg-blue-50 p-6 rounded-lg">
                <ul className="space-y-3 text-gray-700">
                  <li>• Delivery times are estimates and not guaranteed</li>
                  <li>• Risk of loss passes to you upon delivery</li>
                  <li>• You must inspect items upon delivery and report damage immediately</li>
                  <li>• Shipping charges are non-refundable except for our errors</li>
                  <li>• Delivery address changes may incur additional charges</li>
                </ul>
              </div>
            </div>

            {/* Rental Services */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Rental Services</h2>
              <div className="bg-purple-50 p-6 rounded-lg">
                <ul className="space-y-3 text-gray-700">
                  <li>• Rental agreements are separate contracts with specific terms</li>
                  <li>• Security deposits are required for all rentals</li>
                  <li>• You are responsible for the care and return of rental items</li>
                  <li>• Late returns incur daily penalty charges</li>
                  <li>• Damage or loss will be charged at replacement cost</li>
                </ul>
              </div>
            </div>

            {/* Intellectual Property */}
            <div>
              <div className="flex items-center mb-4">
                <Shield className="h-6 w-6 text-purple-600 mr-2" />
                <h2 className="text-2xl font-bold text-gray-900">7. Intellectual Property</h2>
              </div>
              <div className="space-y-4">
                <p className="text-gray-700">
                  All content on our website, including text, graphics, logos, images, and software, 
                  is the property of Strum Music or its licensors and is protected by copyright and 
                  other intellectual property laws.
                </p>
                <div className="bg-purple-50 p-4 rounded-lg">
                  <p className="text-gray-700">
                    You may not reproduce, distribute, modify, or create derivative works from our 
                    content without express written permission.
                  </p>
                </div>
              </div>
            </div>

            {/* Limitation of Liability */}
            <div>
              <div className="flex items-center mb-4">
                <AlertCircle className="h-6 w-6 text-orange-600 mr-2" />
                <h2 className="text-2xl font-bold text-gray-900">8. Limitation of Liability</h2>
              </div>
              <div className="bg-orange-50 p-6 rounded-lg">
                <p className="text-gray-700 mb-4">
                  To the maximum extent permitted by law, Strum Music shall not be liable for any 
                  indirect, incidental, special, consequential, or punitive damages, including but 
                  not limited to:
                </p>
                <ul className="space-y-2 text-gray-700">
                  <li>• Loss of profits or revenue</li>
                  <li>• Loss of data or information</li>
                  <li>• Business interruption</li>
                  <li>• Personal injury (except where caused by our negligence)</li>
                </ul>
              </div>
            </div>

            {/* Governing Law */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Governing Law</h2>
              <p className="text-gray-700">
                These Terms are governed by the laws of India. Any disputes arising from these Terms 
                or your use of our services shall be subject to the exclusive jurisdiction of the 
                courts in Delhi, India.
              </p>
            </div>

            {/* Changes to Terms */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Changes to Terms</h2>
              <div className="bg-yellow-50 p-6 rounded-lg">
                <p className="text-gray-700">
                  We reserve the right to modify these Terms at any time. Changes will be effective 
                  immediately upon posting on our website. Your continued use of our services after 
                  changes are posted constitutes acceptance of the modified Terms.
                </p>
              </div>
            </div>

            {/* Contact Information */}
            <div className="bg-gray-50 p-6 rounded-lg">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Questions About These Terms?</h2>
              <p className="text-gray-700 mb-4">
                If you have any questions about these Terms and Conditions, please contact us:
              </p>
              <div className="space-y-2 text-gray-700">
                <p><strong>Email:</strong> strum.music.co@gmail.com</p>
                <p><strong>Phone:</strong> +91-8076883098</p>
                <p><strong>Address:</strong> C-7/13, Lawrence Road, Keshav Puram, Delhi 110035</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default TermsConditionsPage;